import { IPromise, IQService } from 'angular';
import {
    IProject,
    IProjectFilters,
    IProjectNew,
    IProjectSummary,
} from '../../tn.common';
import { UserService } from '../../tn.user/service.tn.user';
import { ProjectsRepository } from '../projects-repository.service/projectsRepository';

/*
 *  LocalFavorites stores a collection of the users favorites locally that controllers can bind to
 *  This allows the nav-bar and favorites page to stay in sync.
 */
export class LocalProjects {
    summaries: IProjectSummary[] = [];
    summariesIdx: Record<number, IProjectSummary> = {};
    filteredSummaries: IProjectSummary[] = [];

    // stylenames indexed by project id
    styleNames: Record<number, string[]> = {};
    styleNamesPromise?: IPromise<unknown> = undefined;
    _updatePromise?: IPromise<IProjectSummary[]> = undefined;
    filters: IProjectFilters = {};

    static $inject = ['projectsRepository', 'tn.user.service', '$q'];
    constructor(
        protected projectsRepository: ProjectsRepository,
        protected userService: UserService,
        protected $q: IQService
    ) {
        userService.login$.subscribe(() => this.fetchSummaries(true));
        if (userService.isLoggedIn()) {
            this.fetchSummaries(true);
        }
    }

    fetchSummaries(refresh = false): IPromise<IProjectSummary[]> {
        if (!this._updatePromise || refresh) {
            this._updatePromise = this.projectsRepository
                .list()
                .then((summaries) => {
                    this.summaries.splice(0, this.summaries.length);
                    this.summaries.push(...summaries);

                    summaries.forEach((summary) => {
                        this.summariesIdx[summary.id] = summary;
                    });
                    // reset the styleNamesPromise so that we can re-fetch them
                    this.styleNamesPromise = undefined;
                    this.setFilters(this.filters);
                    return summaries;
                })
                .catch((e) => {
                    this._updatePromise = undefined;
                    throw e;
                });
        }
        return this._updatePromise;
    }

    getProjects(refresh = false): IPromise<IProject[]> {
        return this.fetchSummaries(refresh).then((summaries) => {
            const promises = summaries.map((summary) =>
                this.projectsRepository.get(summary.id)
            );
            return this.$q.all(promises);
        });
    }

    _getStyleNames(refresh = false): void {
        if (this.styleNamesPromise && !refresh) return;
        this.styleNamesPromise = this.getProjects()
            .then((projects) => {
                projects.forEach((project) => {
                    this.styleNames[project.id] = project.licenses.map(
                        (license) => license.style.name.toLowerCase()
                    );
                });
            })
            // re-apply filters once styleNames are loaded, since we're deferring loading until
            // the name filter is applied. The hope here is that we can avoid loading all the
            // projects on every page load.
            .then(() => this.setFilters(this.filters));
    }

    getSummaries(): IProjectSummary[] {
        return this.summaries;
    }

    filterSummaries(
        summary: IProjectSummary,
        filters: IProjectFilters
    ): boolean {
        const name = filters.name ? filters.name.toLowerCase() : '';
        if (name) {
            // lazyily load style names once we start searching on name.
            this._getStyleNames();
            const names = [summary.name.toLowerCase()];
            const styleNames = this.styleNames[summary.id] || [];
            names.push(...styleNames);
            if (!names.some((n) => n.includes(name))) return false;
        }

        const startYear = filters.date?.start?.year;
        const startMonth = filters.date?.start?.month;
        if (startYear && startMonth) {
            const startDate = new Date(startYear, parseInt(startMonth) - 1);
            if (startDate > summary.date) return false;
        }

        const endYear = filters.date?.end?.year;
        const endMonth = filters.date?.end?.month;
        if (endYear && endMonth) {
            const endDate = new Date(endYear, parseInt(endMonth) - 1);
            if (endDate < summary.date) return false;
        }
        return true;
    }

    setFilters(newFilters: IProjectFilters): void {
        this.filters = newFilters;
        const filtered = this.summaries.filter((summary) =>
            this.filterSummaries(summary, this.filters)
        );
        this.filteredSummaries.splice(0, this.filteredSummaries.length);
        this.filteredSummaries.push(...filtered);
    }

    getFilteredSummaries(): IProjectSummary[] {
        return this.filteredSummaries;
    }

    deleteProject(id: number): IPromise<unknown> {
        const summaryIndex = this.summaries.findIndex(
            (summary) => summary.id === id
        );
        if (summaryIndex > -1) {
            this.summaries.splice(summaryIndex, 1);
        }
        delete this.summariesIdx[id];
        delete this.styleNames[id];
        return this.projectsRepository.delete(id).then((results) => {
            this.fetchSummaries(true);
            return results;
        });
    }

    saveProject(project: IProjectNew): IPromise<IProject> {
        return this.projectsRepository.save(project).then((results) => {
            this.styleNames[results.id] = results.licenses.map(
                (l) => l.style.name
            );
            this.fetchSummaries(true);
            return results;
        });
    }
}
